import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import styles from './AssociatedProductInput.module.css'
import ProductChip from './ProductChip'
import { useFixedDropdownPosition } from '../hooks/useFixedDropdownPosition'
import { useTheme } from '../context/ThemeContext'

/**
 * AssociatedProductInput — links products to a Knowledge Base source. Typing
 * filters the tenant's product catalog; selections render as ProductChips.
 *
 * Selection is catalog-only by design: the backend links a document to products
 * that already exist in the tenant graph and silently ignores any other name,
 * so a typed-in product that matches nothing can't be added here either.
 *
 * Props:
 *   id                {string}
 *   selected          {string[]} — product names
 *   availableProducts {string[]} — the tenant's catalog
 *   onChange          {function(string[])}
 *   placeholder       {string} — shown only when there are no chips yet
 */
export default function AssociatedProductInput({ id, selected = [], availableProducts = [], onChange, placeholder }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputColRef = useRef(null)
  const dropdownStyle = useFixedDropdownPosition(inputColRef, open)
  const { theme } = useTheme()

  const suggestions = availableProducts
    .filter(name => !selected.includes(name))
    .filter(name => name.toLowerCase().includes(query.trim().toLowerCase()))

  // Shortcut to link the whole catalog at once. Only offered against the
  // unfiltered list (no point once the user's narrowed it by typing), and only
  // while at least one product isn't already linked.
  const showAllOption = query.trim() === '' && availableProducts.length > 0 && selected.length < availableProducts.length

  function addProduct(name) {
    onChange([...selected, name])
    setQuery('')
  }

  function addAllProducts() {
    onChange([...availableProducts])
    setQuery('')
    setOpen(false)
  }

  function removeProduct(name) {
    onChange(selected.filter(n => n !== name))
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setOpen(false)
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      onChange(selected.slice(0, -1))
    } else if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault()
      addProduct(suggestions[0])
    }
  }

  return (
    <div className={styles.wrap}>
      {selected.map(name => (
        <ProductChip key={name} name={name} onRemove={() => removeProduct(name)} />
      ))}

      <div className={styles.inputCol} ref={inputColRef}>
        <input
          id={id}
          className={styles.input}
          type="text"
          placeholder={selected.length ? 'Add another product…' : placeholder}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
        />

        {open && createPortal(
          <div className={styles.dropdown} style={dropdownStyle} data-theme={theme}>
            {showAllOption && (
              <button
                type="button"
                className={`${styles.option} ${styles.optionAll}`}
                onMouseDown={e => e.preventDefault()}
                onClick={addAllProducts}
              >
                <span className={styles.optionName}>All Products</span>
                <span className={styles.optionCount}>{availableProducts.length}</span>
              </button>
            )}
            {suggestions.map(name => (
              <button
                key={name}
                type="button"
                className={styles.option}
                onMouseDown={e => e.preventDefault()}
                onClick={() => addProduct(name)}
              >
                <span className={styles.optionName}>{name}</span>
              </button>
            ))}
            {suggestions.length === 0 && !showAllOption && (
              <div className={styles.emptyOption}>
                {availableProducts.length === 0
                  ? 'No products in this tenant yet'
                  : 'No matching products'}
              </div>
            )}
          </div>,
          document.body,
        )}
      </div>
    </div>
  )
}
